import { BfxBlotterAction } from "../bfx-base-blotter/BfxBlotterAction";
import { BfxBlotterOrdersContextMenuRejectState } from "./BfxBlotterOrdersContextMenuRejectState";
import { BfxBlotterOrdersContextMenuItem } from "./BfxBlotterOrdersContextMenuItem";
import { BfxBlotterOrdersContextMenuItemRejectState } from "./BfxBlotterOrdersContextMenuItemRejectState";
import { BfxBlotterOrdersContextMenuState } from "./BfxBlotterOrdersContextMenuState";
import { ContextMenuItemStateCommand } from "../ContextMenuItemStateCommand";
import { ContextMenuItemStateCommandCriteria } from "../ContextMenuItemStateCommandCriteria";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

export class BfxBlotterOrdersContextMenuRejectItem extends BfxBlotterOrdersContextMenuItem {
  private constructor(
    public actionId: BfxBlotterAction,
    _title: string,
    checkAvailable: ContextMenuItemStateCommand<CubiqTS.OrderDataImpl>,
    checkEnabled: ContextMenuItemStateCommand<CubiqTS.OrderDataImpl>,
    public checkAvailableCriteria: ContextMenuItemStateCommandCriteria = ContextMenuItemStateCommandCriteria.MenuItem,
    public checkEnabledCriteria: ContextMenuItemStateCommandCriteria = ContextMenuItemStateCommandCriteria.Item,
  ) {
    super(
      actionId,
      _title,
      checkAvailable,
      checkEnabled,
      checkAvailableCriteria,
      checkEnabledCriteria,
    );
  }

  public reset(): void {
    let oldState = this.state;
    this.state = new BfxBlotterOrdersContextMenuItemRejectState();
    this.state.title = oldState.title;
  }

  static Factory: any = class BfxBlotterOrdersContextMenuAcceptanceItemFactory {
    constructor() {}
    public Reject(): BfxBlotterOrdersContextMenuRejectItem {
      return new BfxBlotterOrdersContextMenuRejectItem(
        BfxBlotterAction.ACTION_ORDER_REJECT,
        "Reject",
        BfxBlotterOrdersContextMenuRejectItem.checkRejectAvailableFn(),
        BfxBlotterOrdersContextMenuRejectItem.checkRejectEnabled(),
        ContextMenuItemStateCommandCriteria.Item,
        ContextMenuItemStateCommandCriteria.PostItem,
      );
    }
  };

  private static checkProcessItem(
    menuState: BfxBlotterOrdersContextMenuState,
    itemState: BfxBlotterOrdersContextMenuItemRejectState,
    item?: CubiqTS.OrderDataImpl,
    itemIndex?: number,
  ): void {
    if (itemIndex > menuState.reject.processedItemIndex) {
      BfxBlotterOrdersContextMenuRejectItem.processItem(menuState, itemState, item, itemIndex);
      menuState.reject.processedItemIndex = itemIndex;
    }
  }

  private static checkRejectAvailableFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: BfxBlotterOrdersContextMenuItemRejectState,
      item?: CubiqTS.OrderDataImpl,
      itemIndex?: number,
    ) {
      BfxBlotterOrdersContextMenuRejectItem.checkProcessItem(menuState, itemState, item, itemIndex);
      itemState.iconClass = "icon-close order__menu__menuItem__icon__reject";
      itemState.warnings = menuState.reject.warnings;
      if (
        menuState.reject.ineligibleItem ||
        menuState.reject.ineligibleReject ||
        menuState.reject.hasNoElegible
      ) {
        itemState.title = "Reject Selected";
        itemState.available = false;
      } else if (menuState.reject.hasCallOnly) {
        itemState.title = "Reject";
        itemState.available = true;
      } else if (menuState.reject.hasNewPendingOnly) {
        itemState.title = menuState.reject.warnings.external.display
          ? "Force Reject"
          : "Reject New";
        itemState.available = true;
      } else if (menuState.reject.hasModifyOutOnly) {
        itemState.title = menuState.reject.warnings.external.display
          ? "Force Reject"
          : "Reject Modify Out";
        itemState.available = true;
      } else if (menuState.reject.hasModifyInOnly) {
        itemState.title = "Reject Modify In";
        itemState.available = true;
      } else if (menuState.reject.hasMigrationOnly) {
        itemState.title = "Reject Migration";
        itemState.available = false;
      } else if (menuState.reject.hasCancelOnly) {
        itemState.title = menuState.reject.warnings.external.display
          ? "Force Reject"
          : "Reject Cancellation";
        itemState.available = true;
      } else if (menuState.reject.hasExpiryOnly) {
        itemState.title = "Reject Expiry";
        itemState.available = false;
      } else if (menuState.reject.hasTransferOnly) {
        itemState.title = menuState.acceptance.warnings.external.display
          ? "Force Reject Transfer"
          : "Reject Transfer";
        itemState.available = true;
      } else if (menuState.reject.hasPauseOnly) {
        itemState.title = "Reject Pause";
        itemState.available = true;
      } else if (menuState.reject.hasResumeOnly) {
        itemState.title = "Reject Resume";
        itemState.available = true;
      } else {
        itemState.title = menuState.reject.warnings.external.display
          ? "Force Reject"
          : "Reject Selected";
        itemState.available = true;
      }
    };
  }

  private static checkRejectEnabled(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: BfxBlotterOrdersContextMenuItemRejectState,
      item?: CubiqTS.OrderDataImpl,
      itemIndex?: number,
    ) {
      if (menuState.reject.ineligibleItem === false || menuState.reject.ineligibleReject == false) {
        itemState.enabled = true;
        itemState.actionRequests = menuState.reject.actionRequests;
      }
    };
  }

  private static processItem(
    menuState: BfxBlotterOrdersContextMenuState,
    itemState: BfxBlotterOrdersContextMenuItemRejectState,
    item?: CubiqTS.OrderDataImpl,
    itemIndex?: number,
  ) {
    // break early if we can
    if (item == null || menuState.reject.ineligibleItem) {
      return;
    }

    let isRejectItem: boolean = false;
    let isProcessedStrategyItem: boolean = false;

    let isTrader: boolean = CubiqTS.UserType.TRADER === menuState.activeUser.userTypeId;
    // Determine if a group ID represents an external trader group
    const isExternal = (groupId, groupMap: Map<string, CubiqTS.TradingGroupImpl>) => {
      if (groupId != null) {
        const group = groupMap.get(groupId.toString());
        return group?.isExternallyFilledFg ?? false;
      }
      return false;
    };

    const isItemTraderGroupExternal = isExternal(item.deskId, menuState.traderGroups);
    const isItemTargetGroupExternal = isExternal(item.targetDeskId, menuState.traderGroups);

    // Permitted for non-transfers only for a trader with reject role or any user with unrestricted role & transfer if has DI_TRADER_TRANSFER
    const isTransfer: boolean = !!item.targetDeskId && !!item.transferUserId;
    const permitted: boolean = isTransfer
      ? menuState.canTransferAccept
      : (menuState.canReject && isTrader) || menuState.canAcceptUnrestricted;
    let inPauseResumePending: boolean = false;

    if (permitted) {
      const isTraderInGroup =
        item.deskId != null &&
        CubiqTS.UserType.TRADER === menuState.activeUser.userTypeId &&
        menuState.activeUser.tradingGroupsIdSet.has(item.deskId.toString());
      const isTraderAndUnrestricted =
        CubiqTS.UserType.TRADER === menuState.activeUser.userTypeId &&
        menuState.canAcceptUnrestricted;
      const isDefaultTraderGroup = item.deskId != null && "-1" === item.deskId.toString();

      if (
        item.strategyId &&
        menuState.reject.processedSiblingStrategyIds.has(item.strategyId.toString())
      ) {
        // accept only on leg of strategy
        isProcessedStrategyItem = true;
      } else if (
        (CubiqTS.OrderState.ACCEPTANCE_NEEDED === item.orderState ||
          CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === item.orderState) &&
        (isTraderInGroup || menuState.canAcceptUnrestricted)
      ) {
        let requiresCallOrderRateSide =
          CubiqTS.RequestOrderType.CALL === item.requestedOrderType &&
          CubiqTS.RateSideType.BID !== item.trackingSide &&
          CubiqTS.RateSideType.OFFER !== item.trackingSide;

        if (CubiqTS.LockType.MODIFY_LOCK === item.lockType) {
          if (requiresCallOrderRateSide) {
            menuState.reject.callCount++;
          } else {
            menuState.reject.modifyInCount++;
          }
          menuState.reject.addRejectRequest(CubiqTS.AcceptanceType.ENTRY, item.orderId.toString());
        } else {
          if (requiresCallOrderRateSide) {
            menuState.reject.callCount++;
          } else {
            menuState.reject.newPendingCount++;
          }
          menuState.reject.addRejectRequest(CubiqTS.AcceptanceType.ENTRY, item.orderId.toString());
        }

        isRejectItem = true;
      } else if (
        CubiqTS.LockType.PAUSE_LOCK == item.lockType &&
        CubiqTS.PauseState.PAUSE_PENDING == item.pauseState &&
        (isTraderInGroup || menuState.canAcceptUnrestricted)
      ) {
        menuState.reject.pauseCount++;
        menuState.reject.addRejectRequest(CubiqTS.AcceptanceType.PAUSE, item.orderId.toString());
        isRejectItem = true;
        inPauseResumePending = true;
      } else if (
        CubiqTS.LockType.PAUSE_LOCK == item.lockType &&
        CubiqTS.PauseState.RESUME_PENDING == item.pauseState &&
        (isTraderInGroup || menuState.canAcceptUnrestricted)
      ) {
        menuState.reject.resumeCount++;
        menuState.reject.addRejectRequest(CubiqTS.AcceptanceType.RESUME, item.orderId.toString());
        isRejectItem = true;
        inPauseResumePending = true;
      } else if (
        (CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED === item.orderState ||
          CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === item.orderState) &&
        (isTraderInGroup || menuState.canAcceptUnrestricted)
      ) {
        menuState.reject.modifyOutCount++;
        menuState.reject.addRejectRequest(
          CubiqTS.AcceptanceType.MODIFICATION,
          item.orderId.toString(),
        );
        isRejectItem = true;
      } else if (
        (CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED === item.orderState ||
          CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED === item.orderState) &&
        (isTraderInGroup || menuState.canAcceptUnrestricted)
      ) {
        menuState.reject.cancelCount++;
        menuState.reject.addRejectRequest(CubiqTS.AcceptanceType.CANCEL, item.orderId.toString());
        isRejectItem = true;
      } else if (
        (CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED === item.orderState ||
          CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED === item.orderState) &&
        (isTraderInGroup || menuState.canAcceptUnrestricted)
      ) {
        menuState.reject.expiryCount++;
        // no reject is possible
        menuState.reject.ineligibleReject = true;
        isRejectItem = true;
      } else if (CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED === item.orderState) {
        if (isDefaultTraderGroup) {
          menuState.reject.ineligibleReject = true;
          isRejectItem = true;
        } else if ((isTraderInGroup || isTraderAndUnrestricted) && !isItemTraderGroupExternal) {
          menuState.reject.migrationCount++;
          menuState.reject.ineligibleReject = true;
          isRejectItem = true;
        }

        /// TRANSFER - only relies on  ROLE_DI_TRADER_TRANSFER not on ROLE_DI_TRADER_REJECT (OMS-31029, OMS-22330)
      } else if (!!item.targetDeskId && !!item.transferUserId) {
        // FIXME: why is mediator ActiveUser object using number for trading group ids?
        let traderInTargetGroup = menuState.activeUser.tradingGroupsIdSet.has(
          item.targetDeskId.toString(),
        );

        const transferExternal = isItemTraderGroupExternal || isItemTargetGroupExternal;
        const canExternalAction =
          transferExternal &&
          menuState.canTransferAccept &&
          (menuState.canReject || menuState.canAcceptUnrestricted);
        const canInternalAction = !transferExternal && menuState.canTransferAccept;
        // FXBFXOM-10530: Trader can accept if has DI_TRADER_TRANSFER and either in group or has TRADER_UNRESTRICTED

        if (
          (traderInTargetGroup || menuState.canAcceptUnrestricted) &&
          (canExternalAction || canInternalAction)
        ) {
          menuState.reject.transferCount++;
          menuState.reject.addXferRejectRequest(
            item.targetDeskId.toString(),
            item.orderId.toString(),
          );
          isRejectItem = true;
        } else {
          isRejectItem = false;
        }
      }
    }
    if (isRejectItem) {
      menuState.reject.count++;
      // item is with external trader group
      if (
        (isItemTraderGroupExternal &&
          (CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === item.orderState ||
            CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED === item.orderState ||
            CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === item.orderState ||
            inPauseResumePending)) ||
        (isTransfer && (isItemTraderGroupExternal || isItemTargetGroupExternal))
      ) {
        menuState.reject.warnings.show = true;
        menuState.reject.warnings.external.display = true;
        menuState.reject.warnings.external.ids.push(item.orderId.toString());
        menuState.reject.warnings.external.orderRefs.push(item.orderRef);
      }
      if (item.strategyId != null) {
        // && item.siblingRelationship &&
        // CubiqTS.OrderRelationshipType.OCO === item.siblingRelationship.relationshipType ) {
        menuState.reject.processedSiblingStrategyIds.add(item.strategyId.toString());
      }
    } else if (isProcessedStrategyItem) {
      // no impact on counts
    } else {
      menuState.reject.ineligibleItem = true;
    }
  }

  public static get state(): any {
    return new BfxBlotterOrdersContextMenuRejectState();
  }
}
